// adminportal/src/pages/AdminLogin.tsx
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import Copyright from '../components/Copyright';
import Logo from '../components/Logo';
import { useNavigate } from 'react-router-dom';
import { CognitoAuthService } from '../services/cognitoAuth';
import { markAuthed } from '../auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { loadCognitoConfig } from '../config/cognito';

export default function AdminLogin() {
  const { settings } = useSettings();
  const nav = useNavigate();

  // Set page title
  usePageTitle('Admin Login');

  // Load Cognito configuration on component mount
  useEffect(() => {
    const initializeCognito = async () => {
      try {
        const loaded = await loadCognitoConfig();
        if (loaded) {
          console.log('✅ Cognito configuration loaded successfully');
        } else {
          console.warn('⚠️ Cognito configuration not available in config.json');
        }
      } catch (error) {
        console.error('❌ Failed to load Cognito configuration:', error);
      }
    };

    initializeCognito();
  }, []);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      // Attempting Cognito login

      // Use Cognito authentication
      const result = await CognitoAuthService.login(username, password);

      if (result.success) {
        // Login result received
        if (result.userType === 'admin') {
          // Admin user - allow access to admin portal
          // Admin login successful
          markAuthed(); // Set authentication state
          nav('/admin-dashboard');
        } else {
          // Standard/Super user - show access denied message
          // Standard/Super user attempted to access admin portal
          setErrorMessage(
            'Access Denied: Admin portal is restricted to administrators only. Please contact your system administrator if you need access.'
          );
        }
      } else if (result.userType && result.userType !== 'admin') {
        // Standard/Super user - show access denied message
        // Standard/Super user attempted to access admin portal (success: false)
        setErrorMessage(
          'Access Denied: Admin portal is restricted to administrators only. Please contact your system administrator if you need access.'
        );
      } else {
        // Handle different challenge types
        if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // Store session for password change and redirect to admin setup
          if (result.session) {
            localStorage.setItem('adminPasswordChangeSession', result.session);
            localStorage.setItem('adminPasswordChangeUsername', username);
            nav('/admin-setup?step=password-reset');
          } else {
            setErrorMessage('Password change required but no session available.');
          }
        } else if (result.redirectUrl) {
          // Redirect users to appropriate setup page
          // Redirecting to setup page
          if (result.redirectUrl.startsWith('http')) {
            window.location.href = result.redirectUrl;
          } else {
            nav(result.redirectUrl);
          }
        } else {
          setErrorMessage(result.error || 'Login failed');
        }
      }
    } catch (error: unknown) {
      // Login error occurred
      setErrorMessage(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleForgotUsername(e: React.FormEvent) {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');

    if (!forgotEmail) {
      setForgotMessage('❌ Email address is required');
      setForgotLoading(false);
      return;
    }

    try {
      // In a real implementation, you would call your backend API
      // to send the username to the email address
      setForgotMessage('✅ If an account exists with that email, your username has been sent.');
    } catch (error: unknown) {
      setForgotMessage(`❌ ${error instanceof Error ? error.message : 'An error occurred'}`);
    } finally {
      setForgotLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');

    if (!forgotEmail) {
      setForgotMessage('❌ Email address is required');
      setForgotLoading(false);
      return;
    }

    try {
      const result = await CognitoAuthService.forgotPassword(forgotEmail);
      if (result.success) {
        setForgotMessage('✅ Password reset code sent to your email address.');
      } else {
        setForgotMessage(result.error || 'Failed to send password reset code.');
      }
    } catch (error: unknown) {
      setForgotMessage(`❌ ${error instanceof Error ? error.message : 'An error occurred'}`);
    } finally {
      setForgotLoading(false);
    }
  }

  return (
    <div className='min-h-screen flex items-center justify-center relative overflow-hidden'>
      {/* Animated blobs */}
      <div className='absolute inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute -inset-10 opacity-50'>
          <div
            className='absolute top-1/4 left-1/4 w-72 h-72 rounded-full mix-blend-multiply blur-xl animate-pulse'
            style={{ backgroundColor: 'var(--color-primary)' }}
          />
          <div
            className='absolute top-1/3 right-1/4 w-72 h-72 rounded-full mix-blend-multiply blur-xl animate-pulse animation-delay-2000'
            style={{ backgroundColor: 'var(--color-accent)' }}
          />
          <div
            className='absolute bottom-1/4 left-1/3 w-72 h-72 rounded-full mix-blend-multiply blur-xl animate-pulse animation-delay-4000'
            style={{ backgroundColor: 'var(--color-btn-primary)' }}
          />
        </div>
      </div>

      {/* Floating particles */}
      <div className='absolute inset-0 pointer-events-none'>
        {[...Array(20)].map((_, i) => (
          <div key={i} className='floating-particle' />
        ))}
      </div>

      {/* Card */}
      <div className='relative z-10 w-full max-w-md mx-4'>
        <div className='backdrop-blur-2xl rounded-3xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)] border border-primary bg-overlay transition-all duration-300 hover:bg-hover'>
          {/* Header */}
          <div className='text-center mb-8'>
            <div className='mb-6 flex justify-center'>
              <Logo size='xs' className='mx-auto' />
            </div>
            <h1 className='text-3xl font-bold mb-2 tracking-tight text-primary'>
              {settings.title}
            </h1>
            <p className='text-sm text-secondary'>{settings.subtitle}</p>
          </div>

          {/* Form */}
          <form className='space-y-6' onSubmit={handleLogin}>
            <div className='group'>
              <label className='block text-sm font-medium mb-2 text-secondary'>Username</label>
              <div className='relative'>
                <User className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors text-tertiary' />
                <input
                  type='text'
                  placeholder='Enter your username'
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className='w-full pr-4 py-3 rounded-xl outline-none transition-all form-input'
                  style={{ paddingLeft: '3.5rem' }}
                  required
                  autoComplete='username'
                />
              </div>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium mb-2 text-secondary'>Password</label>
              <div className='relative'>
                <Lock className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors text-tertiary' />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Enter your password'
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className='w-full pr-11 py-3 rounded-xl outline-none transition-all form-input'
                  style={{ paddingLeft: '3.5rem' }}
                  required
                  autoComplete='current-password'
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 transition-colors text-tertiary'
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                </button>
              </div>
            </div>

            {errorMessage && (
              <p className='text-sm' style={{ color: 'var(--color-error-text)' }} role='alert'>
                {errorMessage}
              </p>
            )}

            <div className='flex items-center justify-between text-sm'>
              <label className='flex items-center cursor-pointer text-secondary'>
                <input
                  type='checkbox'
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  className='mr-2 rounded form-input'
                />
                Remember me
              </label>
              <div className='flex gap-3'>
                <a href='#' className='transition-colors' style={{ color: 'var(--color-primary)' }}>
                  Forgot username?
                </a>
                <a
                  href='/password-reset'
                  className='transition-colors'
                  style={{ color: 'var(--color-primary)' }}
                >
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type='submit'
              disabled={isLoading}
              className='group w-full font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden disabled:opacity-50 btn-primary'
            >
              {isLoading ? (
                <div className='flex items-center justify-center'>
                  <div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2' />
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
              <span className='pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700' />
            </button>
          </form>

          {/* Account Recovery Section */}
          <div className='mt-8 p-4 bg-white/5 border border-white/10 rounded-xl'>
            <h3 className='text-sm font-medium text-gray-300 mb-3'>
              Need Help Accessing Your Account?
            </h3>

            {forgotMessage && (
              <div
                className={`p-3 rounded-lg mb-4 text-sm ${
                  forgotMessage.startsWith('✅')
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-rose-500/20 text-rose-400'
                }`}
              >
                {forgotMessage}
              </div>
            )}

            <div className='space-y-3'>
              <form onSubmit={handleForgotUsername} className='flex items-center gap-3'>
                <input
                  type='email'
                  placeholder='Enter your email address'
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  className='flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                  required
                />
                <button
                  type='submit'
                  disabled={forgotLoading}
                  className='px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50'
                >
                  {forgotLoading ? 'Sending...' : 'Send Username'}
                </button>
              </form>

              <form onSubmit={handleForgotPassword} className='flex items-center gap-3'>
                <input
                  type='email'
                  placeholder='Enter your email address'
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  className='flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                  required
                />
                <button
                  type='submit'
                  disabled={forgotLoading}
                  className='px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm disabled:opacity-50'
                >
                  {forgotLoading ? 'Sending...' : 'Reset Password'}
                </button>
              </form>
            </div>
          </div>
        </div>

        <Copyright className='mt-6' showEasterEgg={true} />
      </div>
    </div>
  );
}
